import { Activity, Eye, FileBarChart, Inbox, MessagesSquare } from "lucide-react";
import type { ReactNode } from "react";

import { MaxwellActivity } from "@/components/maxwell/MaxwellActivity";
import { MaxwellChat } from "@/components/maxwell/MaxwellChat";
import { MaxwellInbox } from "@/components/maxwell/MaxwellInbox";
import type { TabRoute } from "@/lib/routes";
import MaxwellReportsPage from "@/pages/reports/MaxwellReportsPage";
import WatchesPage from "@/pages/watches/WatchesPage";

/**
 * Bounded scroll pane for the non-chat Maxwell tabs. The workspace tab body is
 * a full-height, non-scrolling flex column (so the Chat tab's own message list
 * is the scroller — the composer stays pinned); every other tab fills that
 * column and scrolls its own content instead of growing the page.
 */
function TabScrollPane({ children }: { children: ReactNode }) {
  return <div className="min-h-0 flex-1 overflow-y-auto pb-6">{children}</div>;
}

/**
 * The Maxwell workspace sections, rendered as nested routes under `/maxwell`
 * (`/maxwell/chat`, `/maxwell/activity`, `/maxwell/inbox`, `/maxwell/watches`,
 * `/maxwell/reports`). Declared here (co-located with the page) so both
 * `APP_ROUTES` and the page's `<RouterTabs />` consume one list — see "Tabbed
 * detail pages" in CLAUDE.md.
 */
export const MAXWELL_TAB_ROUTES: ReadonlyArray<TabRoute> = [
  // Chat owns its own internal scroll (transcript + pinned composer); it is not
  // wrapped in a `TabScrollPane`.
  { path: "chat", label: "Chat", icon: MessagesSquare, element: <MaxwellChat surface="page" /> },
  {
    path: "activity",
    label: "Activity",
    icon: Activity,
    element: (
      <TabScrollPane>
        <MaxwellActivity />
      </TabScrollPane>
    ),
  },
  {
    path: "inbox",
    label: "Inbox",
    icon: Inbox,
    element: (
      <TabScrollPane>
        <MaxwellInbox />
      </TabScrollPane>
    ),
  },
  // What Maxwell is keeping an eye on for the signed-in user. Watches are
  // authored in chat (the `create_watch` capability); this tab lists and
  // cancels them.
  {
    path: "watches",
    label: "Watches",
    icon: Eye,
    element: (
      <TabScrollPane>
        <WatchesPage />
      </TabScrollPane>
    ),
  },
  // Branded PDF report catalog. Internal-tenant users only — mirrors the
  // server-side `allowed()` gate on POST /api/reports/:kind/download (suppliers
  // are 403/404'd there).
  {
    path: "reports",
    label: "Reports",
    icon: FileBarChart,
    element: (
      <TabScrollPane>
        <MaxwellReportsPage />
      </TabScrollPane>
    ),
    requiresInternal: true,
  },
];
